import { useMemo, useState, useRef, useEffect } from "react";
import { ChevronDown, Check } from "lucide-react";
import { SERVICES, SERVICE_CATEGORIES } from "@/data/services";
import { CAD_SERVICES, CAD_SERVICE_CATEGORIES } from "@/data/cadServices";

/**
 * Categorized service-picker for the Contact form.
 *
 * Renders TWO collapsible dropdowns (one per practice). Each dropdown header
 * shows the practice name + selected count; clicking it reveals a categorized
 * grid of tickable services.
 *
 *   1. Survey Technicians Services  — 11 sub-services (under 3 sub-categories)
 *   2. CAD Technicians Services     — 12 sub-services (under 3 sub-categories)
 *
 * `value` is the current form.service string (comma-separated service names).
 * `onChange(newCommaSepString)` is called whenever a checkbox toggles.
 */
export default function ServiceCheckboxPicker({ value, onChange }) {
  // Materialize the two practice groups once.
  const practices = useMemo(
    () => [
      {
        id: "survey",
        title: "Survey Technicians Services",
        emoji: "🧭",
        categories: SERVICE_CATEGORIES.map((cat) => ({
          title: cat.title,
          items: cat.slugs
            .map((slug) => SERVICES.find((s) => s.slug === slug))
            .filter(Boolean),
        })),
      },
      {
        id: "cad",
        title: "CAD Technicians Services",
        emoji: "📐",
        categories: CAD_SERVICE_CATEGORIES.map((cat) => ({
          title: cat.title,
          items: cat.slugs
            .map((slug) => CAD_SERVICES.find((s) => s.slug === slug))
            .filter(Boolean),
        })),
      },
    ],
    []
  );

  // Parse the comma-separated value into a Set of selected service NAMES.
  const selected = useMemo(() => {
    const out = new Set();
    (value || "")
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean)
      .forEach((s) => out.add(s));
    return out;
  }, [value]);

  const toggle = (name) => {
    const next = new Set(selected);
    if (next.has(name)) next.delete(name);
    else next.add(name);
    onChange(Array.from(next).join(", "));
  };

  return (
    <div className="sm:col-span-2" data-testid="contact-service-picker">
      <div className="flex items-center justify-between">
        <label className="mono text-[10px] tracking-[0.22em] uppercase text-slate-500">
          Primary services (tick all that apply)
        </label>
        <span
          className="mono text-[10px] tracking-[0.22em] uppercase text-topo"
          data-testid="contact-service-count"
        >
          {selected.size} selected
        </span>
      </div>

      <div className="mt-2 space-y-2">
        {practices.map((p) => (
          <PracticeDropdown
            key={p.id}
            practice={p}
            selected={selected}
            toggle={toggle}
          />
        ))}
      </div>
    </div>
  );
}

function PracticeDropdown({ practice, selected, toggle }) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const allNames = practice.categories.flatMap((c) => c.items.map((s) => s.name));
  const selectedInPractice = allNames.filter((n) => selected.has(n)).length;

  // Close the panel when the user clicks/taps outside (so it behaves like a
  // proper dropdown rather than an always-open accordion).
  useEffect(() => {
    if (!open) return undefined;
    const onDocPointerDown = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("pointerdown", onDocPointerDown);
    return () => document.removeEventListener("pointerdown", onDocPointerDown);
  }, [open]);

  return (
    <div
      ref={containerRef}
      data-testid={`contact-practice-${practice.id}`}
      className="relative"
    >
      <button
        type="button"
        data-testid={`contact-practice-toggle-${practice.id}`}
        onClick={() => setOpen((v) => !v)}
        className={`w-full flex items-center justify-between gap-3 border border-slate-300 bg-white px-4 py-3 text-left rounded-sm transition-colors ${
          open ? "border-topo ring-2 ring-topo/15" : "hover:border-slate-400"
        }`}
        aria-expanded={open}
      >
        <span className="flex items-center gap-2 min-w-0">
          <span className="text-base">{practice.emoji}</span>
          <span className="display text-sm font-extrabold tracking-tight text-slate-900 truncate">
            {practice.title}
          </span>
          <span className="mono text-[10px] tracking-[0.22em] uppercase text-slate-500 hidden sm:inline">
            · {allNames.length} services
          </span>
        </span>
        <span className="flex items-center gap-2 shrink-0">
          {selectedInPractice > 0 && (
            <span
              data-testid={`contact-practice-count-${practice.id}`}
              className="mono text-[10px] tracking-[0.22em] uppercase bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-sm"
            >
              {selectedInPractice} ticked
            </span>
          )}
          <ChevronDown
            className={`h-4 w-4 text-slate-500 transition-transform ${
              open ? "rotate-180" : ""
            }`}
          />
        </span>
      </button>

      {open && (
        <div
          data-testid={`contact-practice-panel-${practice.id}`}
          className="absolute z-20 left-0 right-0 mt-1 bg-white border border-slate-300 rounded-sm shadow-xl p-4 max-h-[60vh] overflow-y-auto"
        >
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-4 gap-y-4">
            {practice.categories.map((cat) => (
              <div key={cat.title}>
                <div className="mono text-[10px] tracking-[0.22em] uppercase text-topo mb-1.5">
                  {cat.title}
                </div>
                <ul className="space-y-1">
                  {cat.items.map((s) => (
                    <ServiceCheckbox
                      key={s.slug}
                      service={s}
                      practiceId={practice.id}
                      checked={selected.has(s.name)}
                      onToggle={() => toggle(s.name)}
                    />
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between">
            <span className="mono text-[10px] tracking-[0.22em] uppercase text-slate-500">
              {selectedInPractice} of {allNames.length} ticked
            </span>
            <button
              type="button"
              data-testid={`contact-practice-done-${practice.id}`}
              onClick={() => setOpen(false)}
              className="inline-flex items-center gap-1.5 rounded-sm bg-slate-900 hover:bg-black text-white text-xs font-semibold px-3 py-1.5"
            >
              <Check className="h-3.5 w-3.5" /> Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function ServiceCheckbox({ service, practiceId, checked, onToggle }) {
  return (
    <li>
      <label
        data-testid={`contact-service-${practiceId}-${service.slug}`}
        className={`flex items-start gap-2 px-2 py-1.5 -mx-2 rounded-sm cursor-pointer transition-colors ${
          checked ? "bg-emerald-50" : "hover:bg-slate-50"
        }`}
      >
        <input
          type="checkbox"
          data-testid={`contact-service-cb-${service.slug}`}
          checked={checked}
          onChange={onToggle}
          className="mt-0.5 h-4 w-4 accent-orange-600 shrink-0 cursor-pointer"
        />
        <span className="text-[13px] leading-snug text-slate-800">
          <span className="mr-1">{service.emoji}</span>
          {service.name}
        </span>
      </label>
    </li>
  );
}
